import '@mui/x-date-pickers/themeAugmentation'

import type { ThemeOptions } from '@mui/material/styles'

import type { ThemeColors } from '../types'

function getInputBoxShadow(color: string) {
  return `0 0 0 2px ${color}`
}

export const inputComponents = (colors: ThemeColors): ThemeOptions['components'] => ({
  MuiAutocomplete: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          padding: 0,
        },
        '& .MuiOutlinedInput-root .MuiAutocomplete-input': {
          padding: '23px 12px 10px 12px',
        },
      },
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: {
        '&.Mui-error': {
          color: colors.error.dark,
        },
      },
    },
  },
  MuiInput: {
    styleOverrides: {
      input: {
        '&:-webkit-autofill': {
          WebkitTextFillColor: colors.main[1],
          // Chrome paints its own near-white fill behind autofilled values; the
          // inset shadow is the only way to replace it.
          boxShadow: `0 0 0 1000px ${colors.autofill} inset`,
        },
        '&::placeholder': {
          opacity: '1 !important' as any,
        },
        color: colors.main[1],
      },
      underline: {
        '&:after': {
          borderBottomColor: colors.active[1],
        },
      },
    },
  },
  MuiInputBase: {
    styleOverrides: {
      input: {
        '&.Mui-disabled': {
          color: colors.disabled,
        },
        '&::placeholder': {
          color: colors.main[3],
          opacity: '1 !important' as any,
        },
        borderRadius: '6px',
      },
      root: {
        borderRadius: '6px',
      },
    },
  },
  MuiInputLabel: {
    defaultProps: {
      variant: 'outlined',
    },
    styleOverrides: {
      formControl: {
        transform: 'translate(0, 1.5px) scale(0.75)',
        transformOrigin: 'top left',
        width: '100%',
      },
      outlined: {
        '&.MuiInputLabel-shrink': {
          transform: 'translate(12px, 9px) scale(0.75)',
        },
        transform: 'translate(14px, 18px) scale(1)',
      },
      root: {
        '&&.Mui-disabled': {
          color: colors.main[4],
        },
        '&&.Mui-focused': {
          '&:not(.Mui-error)': {
            color: colors.active[1],
          },
        },
        // Calmer errors: the label keeps its neutral color; the soft red
        // border + helper text carry the error. Focus sharpens it.
        '&.Mui-error': {
          '&.Mui-focused': {
            color: colors.error.dark,
          },
          color: colors.main[3],
        },
        '&:not(.Mui-error):hover label': {
          color: colors.main[4],
        },
        color: colors.main[3],
      },
      shrink: {
        // Negates x0.75 scale
        transition: 'width 200ms',
        width: '133%',
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      input: {
        '&&.Mui-disabled': {
          backgroundColor: undefined,
        },
        '&:-webkit-autofill': {
          WebkitTextFillColor: colors.main[1],
          borderRadius: 6,
          boxShadow: `0 0 0 1000px ${colors.autofill} inset`,
        },
        color: colors.main[1],
        padding: '23px 12px 10px 12px',
      },
      multiline: {
        '& .MuiOutlinedInput-input': {
          left: -2,
          padding: '10px 0',
          position: 'relative',
        },
        '&.Mui-disabled': {
          background: colors.background.default,
        },
      },
      notchedOutline: {
        // It's so much easier to put it here with important tag rather than
        // override in multiple places using cascade composition
        borderWidth: '1px !important',
      },
      root: {
        '& fieldset': {
          top: 0,
        },
        '& legend': {
          display: 'none',
        },
        '&.Mui-disabled': {
          '& fieldset': {
            borderColor: `${colors.disabled} !important`,
          },
          '& input, & textarea': {
            backgroundColor: colors.background.default,
            color: colors.main[3],
          },
          backgroundColor: colors.background.default,
          boxShadow: '0 0 0 0 transparent !important',
        },
        // Calmer errors: soft red border at rest, focus sharpens to full red
        '&.Mui-error': {
          '&& fieldset': {
            borderColor: colors.error.mid,
          },
          '&.Mui-focused': {
            '&& fieldset': {
              borderColor: colors.error.dark,
            },
            '&:hover': {
              boxShadow: getInputBoxShadow(colors.error.light),
            },
            boxShadow: getInputBoxShadow(colors.error.light),
          },
          '&:hover': {
            boxShadow: getInputBoxShadow(colors.error.light),
          },
          boxShadow: 'none',
        },
        '&.Mui-focused': {
          '& input': {
            '& fieldset': {
              borderColor: colors.active[1],
            },
            '&::placeholder': {
              opacity: [[1], '!important'] as any,
            },
            color: colors.main[1],
          },
          '&, &:hover': {
            boxShadow: getInputBoxShadow(colors.active[3]),
          },
        },
        '&:hover': {
          '& input': {
            color: colors.main[1],
          },
          '&&&': {
            '& fieldset': {
              borderColor: colors.active[1],
            },
          },
          boxShadow: getInputBoxShadow(colors.active[5]),
        },
        fontWeight: 400,
        top: 0,
        transition: 'box-shadow 200ms',
      },
    },
  },
  MuiPickersOutlinedInput: {
    styleOverrides: {
      notchedOutline: {
        // It's so much easier to put it here with important tag rather than
        // override in multiple places using cascade composition
        borderWidth: '1px !important',
      },
      root: {
        '& fieldset': {
          top: 0,
        },
        '& legend': {
          display: 'none',
        },
        '&.Mui-disabled': {
          '& fieldset': {
            borderColor: `${colors.disabled} !important`,
          },
          '& input, & textarea': {
            backgroundColor: colors.background.default,
            color: colors.main[3],
          },
          backgroundColor: colors.background.default,
          boxShadow: '0 0 0 0 transparent !important',
        },
        // Calmer errors: soft red border at rest, focus sharpens to full red
        '&.Mui-error': {
          '&& fieldset': {
            borderColor: colors.error.mid,
          },
          '&.Mui-focused': {
            '&& fieldset': {
              borderColor: colors.error.dark,
            },
            '&:hover': {
              boxShadow: getInputBoxShadow(colors.error.light),
            },
            boxShadow: getInputBoxShadow(colors.error.light),
          },
          '&:hover': {
            boxShadow: getInputBoxShadow(colors.error.light),
          },
          boxShadow: 'none',
        },
        '&.Mui-focused': {
          '& input': {
            '& fieldset': {
              borderColor: colors.active[1],
            },
            '&::placeholder': {
              opacity: [[1], '!important'] as any,
            },
            color: colors.main[1],
          },
          '&, &:hover': {
            boxShadow: getInputBoxShadow(colors.active[3]),
          },
        },
        '&:hover': {
          '& input': {
            color: colors.main[1],
          },
          '&&&': {
            '& fieldset': {
              borderColor: colors.active[1],
            },
          },
          boxShadow: getInputBoxShadow(colors.active[5]),
        },
        fontWeight: 400,
        top: 0,
        transition: 'box-shadow 200ms',
      },
      sectionsContainer: {
        color: colors.main[1],
        padding: '23px 0px 10px 0px',
      },
    },
  },
})
